import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

/**
 * Unwraps the wrappers Angular / zone.js commonly place around a thrown value
 * so we can inspect (and report) the real underlying error.
 */
function unwrapError(error: unknown): unknown {
    let current = error;

    // Angular stores the original error on `ngOriginalError` when it re-throws,
    // and zone.js exposes unhandled promise rejection reasons on `rejection`.
    for (let i = 0; i < 5 && current && typeof current === 'object'; i++) {
        const next = (current as { ngOriginalError?: unknown }).ngOriginalError ?? (current as { rejection?: unknown }).rejection;

        if (next === undefined || next === null || next === current) {
            break;
        }
        current = next;
    }

    return current;
}

/**
 * Coerces a thrown value into a real `Error`.
 *
 * Real `Error` instances (and error-like objects that already carry a usable
 * `name`/`message`) are returned untouched so Bugsnag keeps their stack trace
 * and grouping. Anything else — plain objects, `null`, strings, etc. — is
 * wrapped in an `Error` carrying a best-effort description, so Bugsnag reports
 * something actionable instead of the generic "received a non-error"
 * InvalidError.
 */
export function normalizeError(error: unknown): unknown {
    const unwrapped = unwrapError(error);

    if (unwrapped instanceof Error) {
        return unwrapped;
    }

    // Error-like objects with both a name and a message are handled correctly
    // by Bugsnag already, so leave them as-is.
    if (
        unwrapped &&
        typeof unwrapped === 'object' &&
        typeof (unwrapped as { name?: unknown }).name === 'string' &&
        typeof (unwrapped as { message?: unknown }).message === 'string'
    ) {
        return unwrapped;
    }

    let description: string;
    try {
        if (typeof unwrapped === 'string') {
            description = unwrapped;
        } else {
            const json = JSON.stringify(unwrapped);
            description = json && json !== '{}' ? json : String(unwrapped);
        }
    } catch {
        description = String(unwrapped);
    }

    const wrapped = new Error(description || 'Non-error value thrown');
    wrapped.name = 'NonErrorThrown';
    return wrapped;
}

/**
 * Bugsnag's Angular error handler reports an `InvalidError` ("received a
 * non-error") whenever a non-`Error` value bubbles up to Angular's global
 * `ErrorHandler`. This subclass normalizes the value first so the real
 * underlying problem is captured instead.
 */
export class CdrcErrorHandler extends BugsnagErrorHandler {
    override handleError(error: unknown): void {
        super.handleError(normalizeError(error));
    }
}
